import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Download, Smartphone, BookOpen, RefreshCw, ShieldCheck, AlertCircle } from "lucide-react";

export default function MyEbooks() {
  const { data: ebooks = [], isLoading, refetch } = useQuery({ queryKey: ["my-ebooks"], queryFn: async () => (await api.get("/my/ebooks")).data });
  const [downloading, setDownloading] = useState(null);

  const download = async (e) => {
    setDownloading(e.product_id);
    try {
      const res = await api.get(`/downloads/${e.product_id}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${e.slug}.pdf`; a.click();
      URL.revokeObjectURL(url);
      toast.success("Téléchargement démarré");
      refetch();
    } catch (err) {
      let msg = apiError(err);
      if (err?.response?.data instanceof Blob) { try { msg = JSON.parse(await err.response.data.text()).detail; } catch {} }
      toast.error(msg);
    } finally { setDownloading(null); }
  };

  if (isLoading) return <div className="py-16 text-center text-ink-muted">Chargement…</div>;
  if (ebooks.length === 0) return (
    <div className="bg-cream border border-line rounded-lg p-12 text-center">
      <BookOpen className="h-10 w-10 text-line mx-auto mb-3" />
      <h2 className="font-heading text-xl text-ink">Aucun eBook pour l'instant</h2>
      <p className="text-ink-muted text-sm mt-2">Découvrez notre collection et commencez votre lecture.</p>
      <Button asChild className="mt-5 bg-terracotta hover:bg-terracotta-dark text-white"><Link to="/boutique">Voir la boutique</Link></Button>
    </div>
  );

  return (
    <div>
      <h2 className="font-heading text-2xl text-ink mb-2">Mes eBooks</h2>
      <p className="text-sm text-ink-muted mb-5 flex items-start gap-2">
        <ShieldCheck className="h-4 w-4 text-sage mt-0.5 shrink-0" />
        Chaque téléchargement est un <strong className="font-semibold text-ink">exemplaire personnel</strong>, marqué à votre nom et lié à votre appareil. Merci de ne pas le partager.
      </p>
      <div className="space-y-4">
        {ebooks.map((e) => (
          <div key={e.entitlement_id} className="bg-cream border border-line rounded-lg p-5 flex flex-col sm:flex-row gap-4" data-testid={`ebook-${e.slug}`}>
            <img src={e.cover_url} alt={e.title} className="w-20 h-28 object-contain rounded-sm bg-sand mx-auto sm:mx-0" />
            <div className="flex-1">
              <h3 className="font-heading text-lg text-ink">{e.title}</h3>
              <p className="text-xs text-ink-muted mt-1">Acheté le {new Date(e.purchased_at).toLocaleDateString("fr-FR")} · Commande {e.order_number}</p>
              <div className="flex flex-wrap items-center gap-3 mt-2 text-xs">
                {e.download_blocked
                  ? <span className="inline-flex items-center gap-1 text-destructive"><span className="h-1.5 w-1.5 rounded-full bg-destructive" /> Accès suspendu</span>
                  : <span className="inline-flex items-center gap-1 text-sage"><span className="h-1.5 w-1.5 rounded-full bg-sage" /> Actif</span>}
                <span className="inline-flex items-center gap-1 text-ink-muted"><Smartphone className="h-3.5 w-3.5" /> {e.authorized_device_id ? "Appareil associé" : "Aucun appareil associé"}</span>
                <span className="text-ink-muted">{e.download_count} téléchargement(s)</span>
              </div>
              {e.download_blocked && (
                <p className="text-xs text-destructive mt-2 flex items-start gap-1.5" data-testid={`blocked-${e.slug}`}>
                  <AlertCircle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                  Accès temporairement suspendu suite à des téléchargements depuis plusieurs appareils. Notre support vérifie votre compte — contactez-nous pour le rétablir.
                </p>
              )}
            </div>
            <div className="flex sm:flex-col gap-2 justify-center">
              <Button onClick={() => download(e)} disabled={downloading === e.product_id || e.download_blocked} className="bg-terracotta hover:bg-terracotta-dark text-white rounded-md disabled:opacity-50" data-testid={`download-${e.slug}`}>
                <Download className="mr-2 h-4 w-4" /> {e.download_blocked ? "Suspendu" : (downloading === e.product_id ? "…" : "Télécharger")}
              </Button>
              <DeviceChangeDialog ebook={e} onDone={refetch} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function DeviceChangeDialog({ ebook }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const submit = async () => {
    try {
      await api.post("/device-change", { product_id: ebook.product_id, reason });
      toast.success("Demande envoyée au support.");
      setOpen(false); setReason("");
    } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="border-line text-ink-muted rounded-md" data-testid={`device-change-${ebook.slug}`}><RefreshCw className="mr-2 h-3.5 w-3.5" /> J'ai changé d'appareil</Button>
      </DialogTrigger>
      <DialogContent className="bg-sand">
        <DialogHeader><DialogTitle className="font-heading">Changement d'appareil</DialogTitle></DialogHeader>
        <p className="text-sm text-ink-muted">Décrivez brièvement votre situation. Notre support réinitialisera l'appareil autorisé après vérification.</p>
        <Textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Ex : j'ai changé de téléphone…" className="bg-cream border-line" data-testid="device-change-reason" />
        <DialogFooter><Button onClick={submit} className="bg-terracotta hover:bg-terracotta-dark text-white" data-testid="device-change-submit">Envoyer la demande</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
